import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { PLATFORM } from "../env";
import type { Contract, Driver, DriverTransaction, TenantDriverMembership } from "../types";

interface Props {
  id: number;
  onBack: () => void;
  onOpenDriver: (id: number) => void;
  onOpenTenant: (id: number) => void;
}

interface MembershipPage { items: TenantDriverMembership[]; }

const statusColor = (status: string): "success" | "warning" | "error" | "default" => {
  if (status === "active") return "success";
  if (status === "suspended") return "warning";
  if (status === "blocked") return "error";
  return "default";
};

const authMethodTypeLabel = (type: string) => {
  if (type === "rfid_card") return "RFID Card";
  if (type === "app_rfid") return "App RFID";
  return "App";
};

export default function DriverDetail({ id, onBack, onOpenDriver: _onOpenDriver, onOpenTenant }: Props) {
  const authApi = useAuthApi();
  const [driver, setDriver] = useState<Driver | null>(null);
  const [memberships, setMemberships] = useState<TenantDriverMembership[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [transactions, setTransactions] = useState<DriverTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actioning, setActioning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      authApi<Driver>(`${PLATFORM}/drivers/${id}`),
      authApi<MembershipPage>(`${PLATFORM}/memberships?driver_id=${id}&limit=50&offset=0`),
      authApi<Contract[]>(`${PLATFORM}/drivers/${id}/contracts`),
      authApi<DriverTransaction[]>(`${PLATFORM}/drivers/${id}/transactions?limit=50&offset=0`),
    ])
      .then(([d, m, c, t]) => {
        if (cancelled) return;
        setDriver(d);
        setMemberships(m?.items ?? []);
        setContracts(c ?? []);
        setTransactions(t ?? []);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load driver");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [authApi, id]);

  const patchStatus = async (status: string) => {
    setActioning(true);
    setActionError(null);
    try {
      const updated = await authApi<Driver>(`${PLATFORM}/drivers/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      if (updated) setDriver(updated);
    } catch (e: unknown) {
      setActionError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setActioning(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!driver) return null;

  return (
    <Box>
      <Button onClick={onBack} sx={{ mb: 2 }}>← Back</Button>

      {/* Profile card */}
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2, p: 3, mb: 3, boxShadow: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h5" fontWeight={600}>{driver.name}</Typography>
          <Chip label={driver.status} color={statusColor(driver.status)} />
        </Box>
        <Typography variant="body2" color="text.secondary">Email: {driver.email}</Typography>
        {driver.phone && <Typography variant="body2" color="text.secondary">Phone: {driver.phone}</Typography>}
        <Typography variant="body2" color="text.secondary">
          Created: {new Date(driver.created_at).toLocaleString()}
        </Typography>

        {/* Action buttons */}
        <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
          {driver.status !== "active" && (
            <Button variant="contained" color="success" disabled={actioning} onClick={() => patchStatus("active")}>
              Activate
            </Button>
          )}
          {driver.status !== "suspended" && (
            <Button variant="contained" color="warning" disabled={actioning} onClick={() => patchStatus("suspended")}>
              Suspend platform-wide
            </Button>
          )}
          {driver.status !== "blocked" && (
            <Button variant="contained" color="error" disabled={actioning} onClick={() => patchStatus("blocked")}>
              Block
            </Button>
          )}
        </Box>
        {actionError && <Alert severity="error" sx={{ mt: 1 }}>{actionError}</Alert>}
      </Box>

      {/* Labels */}
      {driver.labels && driver.labels.length > 0 && (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>Labels</Typography>
          <Table size="small" sx={{ mb: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {driver.labels.map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.key}</TableCell>
                  <TableCell>{l.value}</TableCell>
                  <TableCell>{new Date(l.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Divider sx={{ mb: 3 }} />
        </>
      )}

      {/* Auth Methods */}
      <Typography variant="h6" sx={{ mb: 1 }}>Auth Methods</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Identifier</TableCell>
            <TableCell>Label</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Tenant</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(driver.auth_methods ?? []).map((m) => (
            <TableRow key={m.id}>
              <TableCell>{authMethodTypeLabel(m.type)}</TableCell>
              <TableCell>{m.identifier ?? "—"}</TableCell>
              <TableCell>{m.label ?? "—"}</TableCell>
              <TableCell>
                <Chip label={m.status} color={m.status === "active" ? "success" : "default"} size="small" />
              </TableCell>
              <TableCell>{m.tenant_slug ? m.tenant_slug : "central"}</TableCell>
              <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
          {(driver.auth_methods ?? []).length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary">No auth methods.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Contracts */}
      <Typography variant="h6" sx={{ mb: 1 }}>Contracts</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Contract</TableCell>
            <TableCell>Plan</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Valid From</TableCell>
            <TableCell>Valid Until</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {contracts.map((c) => (
            <TableRow key={c.id}>
              <TableCell>{c.id}</TableCell>
              <TableCell>{c.name?.trim() ? c.name : "—"}</TableCell>
              <TableCell>{c.plan?.name?.trim() ? c.plan.name : `#${c.plan_id}`}</TableCell>
              <TableCell>
                <Chip
                  label={c.is_active ? "active" : "inactive"}
                  color={c.is_active ? "success" : "default"}
                  size="small"
                />
              </TableCell>
              <TableCell>{c.valid_from ? new Date(c.valid_from).toLocaleDateString() : "—"}</TableCell>
              <TableCell>{c.valid_until ? new Date(c.valid_until).toLocaleDateString() : "—"}</TableCell>
              <TableCell>{new Date(c.created_at).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
          {contracts.length === 0 && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="text.secondary">No contracts.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Transactions */}
      <Typography variant="h6" sx={{ mb: 1 }}>Transactions</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Charger</TableCell>
            <TableCell>Connector</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Start</TableCell>
            <TableCell>Stop</TableCell>
            <TableCell align="right">Energy (kWh)</TableCell>
            <TableCell align="right">Cost</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {transactions.map((t) => (
            <TableRow key={t.id}>
              <TableCell>{t.id}</TableCell>
              <TableCell>{t.charge_point_id}</TableCell>
              <TableCell>{t.connector_id ?? "—"}</TableCell>
              <TableCell>
                <Chip label={t.status} size="small" />
              </TableCell>
              <TableCell>{t.start_time ? new Date(t.start_time).toLocaleString() : "—"}</TableCell>
              <TableCell>{t.stop_time ? new Date(t.stop_time).toLocaleString() : "—"}</TableCell>
              <TableCell align="right">{typeof t.energy_kwh === "number" ? t.energy_kwh.toFixed(3) : "—"}</TableCell>
              <TableCell align="right">
                {typeof t.cost === "number" ? `${t.cost.toFixed(2)} ${t.currency ?? ""}`.trim() : "—"}
              </TableCell>
              <TableCell>{new Date(t.created_at).toLocaleString()}</TableCell>
            </TableRow>
          ))}
          {transactions.length === 0 && (
            <TableRow>
              <TableCell colSpan={9}>
                <Typography variant="body2" color="text.secondary">No transactions.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Memberships */}
      <Typography variant="h6" sx={{ mb: 1 }}>Memberships</Typography>
      <Table size="small" sx={{ mb: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Tenant ID</TableCell>
            <TableCell>Join Mode</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {memberships.map((m) => (
            <TableRow key={m.id}>
              <TableCell>{m.id}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => onOpenTenant(m.tenant_id)}>
                  {m.tenant_id}
                </Button>
              </TableCell>
              <TableCell>{m.join_mode}</TableCell>
              <TableCell>
                <Chip label={m.status} color={m.status === "active" ? "success" : "warning"} size="small" />
              </TableCell>
              <TableCell>{new Date(m.created_at).toLocaleDateString()}</TableCell>
            </TableRow>
          ))}
          {memberships.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">No memberships.</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

    </Box>
  );
}
